import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppKit } from '@reown/appkit/react';
import { useAccount, useReadContract, useSendTransaction, useSignMessage, useWaitForTransactionReceipt, useBalance } from 'wagmi';
import { parseEther, formatEther } from 'viem';
import { CreditCard, Wallet, Zap, Coins, ArrowRight, ArrowUpRight, Activity, ArrowDown, Server, Cpu, PieChart, Gift, Download, TrendingUp, BarChart2 } from 'lucide-react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts3DModule from 'highcharts/highcharts-3d';

if (typeof Highcharts === 'object' && !Highcharts.has3d) {
  const init3D = Highcharts3DModule.default || Highcharts3DModule;
  if (typeof init3D === 'function') {
    init3D(Highcharts);
  } else if (typeof init3D === 'object' && typeof init3D.default === 'function') {
    init3D.default(Highcharts);
  }
  Highcharts.has3d = true;
}

const API_URL = process.env.REACT_APP_API_URL || '';

function getAuthHeaders(userId) {
  return { 'Content-Type': 'application/json', 'x-user-id': userId };
}

const DGCC_PACKS = [
  { id: 'starter', label: 'Starter',  amount: 5 },
  { id: 'popular', label: 'Popular',  amount: 25 },
  { id: 'pro',     label: 'Pro',      amount: 100 },
  { id: 'elite',   label: 'Elite',    amount: 500 },
  { id: 'whale',   label: 'Whale',    amount: 1000 },
];

// Reusable SVG Sparkline
const Sparkline = ({ data, color, width = 60, height = 24 }) => {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((d - min) / range) * height;
    return `${x},${y}`;
  }).join(' L ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={`grad-${color}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M 0,${height} L ${points} L ${width},${height} Z`} fill={`url(#grad-${color})`} />
      <path d={`M ${points}`} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// 3D Highcharts Donut Chart (Asset Allocation)
const DonutChart = () => {
  const options = React.useMemo(() => ({
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      options3d: { enabled: true, alpha: 45 },
      height: 220,
      margin: [0, 0, 0, 0],
      spacing: [0, 0, 0, 0]
    },
    title: { text: null },
    credits: { enabled: false },
    tooltip: { pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>' },
    plotOptions: {
      pie: {
        innerSize: 90,
        depth: 40,
        dataLabels: { enabled: false },
        showInLegend: false,
        colors: ['#0071E3', '#34C759', '#A78BFA', '#FF9500']
      }
    },
    series: [{
      name: 'Allocation',
      data: [
        ['Fiat USD', 35],
        ['USDT (Web3)', 45],
        ['DGCC', 15],
        ['ENERGY', 5]
      ]
    }]
  }), []);

  const segments = [
    { label: 'Fiat USD', value: 35, color: '#0071E3' },
    { label: 'USDT (Web3)', value: 45, color: '#34C759' },
    { label: 'DGCC', value: 15, color: '#A78BFA' },
    { label: 'ENERGY', value: 5, color: '#FF9500' }
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '16px' }}>
      <div style={{ width: '220px', height: '220px', marginLeft: '-20px' }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', flex: 1, paddingRight: '16px' }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '15px', fontWeight: 600 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#1D1D1F' }}>
              <div style={{ width: 12, height: 12, borderRadius: '3px', background: seg.color }} /> {seg.label}
            </div>
            <div style={{ color: '#86868B' }}>{seg.value}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Apple-like Bento Box
const BentoBox = ({ children, style = {}, className = "", delay = 0, noPadding = false, variant = 'default' }) => {
  const background = variant === 'dark' ? '#000000' : '#FFFFFF';
  const border = variant === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.06)';
  const boxShadow = variant === 'dark' ? '0 10px 40px rgba(0,0,0,0.2)' : '0 2px 12px rgba(0,0,0,0.03)';
  
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }} className={className}
      style={{ background, border, boxShadow, borderRadius: '24px', padding: noPadding ? '0' : '28px', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', ...style }}>
      {children}
    </motion.div>
  );
};

export function WalletPage({ userId, dgccData, refreshBalance }) {
  const [activeTab, setActiveTab] = useState('fiat');
  const [selectedId, setSelectedId] = useState('popular');
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cryptoNetwork, setCryptoNetwork] = useState('erc20');
  const [cryptoAmount, setCryptoAmount] = useState('');
  const [cryptoChecked, setCryptoChecked] = useState(false);
  const [dgccAmount, setDgccAmount] = useState('');
  const [energyAmount, setEnergyAmount] = useState('');
  const [transactions, setTransactions] = useState([]);
  
  useEffect(() => {
    if (!userId) return;
    fetch(`${API_URL}/api/billing/transactions?limit=10`, { headers: getAuthHeaders(userId) })
      .then(r => r.json())
      .then(d => { if (d.success) setTransactions(d.transactions || []); })
      .catch(console.error);
  }, [userId]);
  
  // Web3 State & Hooks
  const { open } = useAppKit();
  const { address, isConnected } = useAccount();
  const dgccQuery = useBalance({ address, chainId: 707070, query: { enabled: !!address } });
  const dgccBalanceData = dgccQuery.data;
  
  const energyContract = '0x703618Ff78F1eF1f6a6C935dB21D8B9be72CCCe3';
  const { data: energyBalanceData, refetch: refetchEnergy } = useReadContract({
    address: energyContract,
    abi: [{ name: 'getClaimedBalance', type: 'function', stateMutability: 'view', inputs: [{ name: 'user', type: 'address' }], outputs: [{ type: 'uint256' }] }],
    functionName: 'getClaimedBalance',
    args: [address],
    chainId: 707070,
    query: { enabled: !!address }
  });

  const formattedEnergy = energyBalanceData ? (Number(energyBalanceData) / 1e18).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0';
  const formattedDgcc = dgccBalanceData ? (Number(dgccBalanceData.value) / 10 ** dgccBalanceData.decimals).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0.00';

  const { sendTransactionAsync } = useSendTransaction();
  const { signMessageAsync } = useSignMessage();

  const selected = DGCC_PACKS.find(p => p.id === selectedId);
  const amount = isCustom ? (parseInt(customAmount) || 0) : (selected?.amount || 0);
  const valid = amount >= 5;

  const handleStripeCheckout = async () => {
    if (!userId) return setError('Please sign in to continue.');
    if (!valid)  return setError('Minimum purchase is $5.00.');
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/billing/create-custom-checkout`, {
        method: 'POST', headers: getAuthHeaders(userId), body: JSON.stringify({ amountUsd: amount }),
      });
      const data = await res.json();
      if (data.checkoutUrl) window.location.href = data.checkoutUrl;
      else setError(data.error || 'Unable to create checkout session.');
    } catch { setError('Network error. Please try again.'); } finally { setLoading(false); }
  };

  const handleNowPaymentsCheckout = async () => {
    if (!userId) return setError('Please sign in to continue.');
    const amt = parseFloat(cryptoAmount);
    if (!amt || amt < 5) return setError('Minimum deposit is $5.00.');
    if (!cryptoChecked) return setError('Please confirm you are using a personal wallet.');
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/billing/create-nowpayments-checkout`, {
        method: 'POST', headers: getAuthHeaders(userId), body: JSON.stringify({ amountUsd: amt, cryptoNetwork }),
      });
      const data = await res.json();
      if (data.invoiceUrl) window.open(data.invoiceUrl, '_blank');
      else setError(data.error || 'Unable to create crypto checkout session.');
    } catch { setError('Network error. Please try again.'); } finally { setLoading(false); }
  };

  const handleDgccDeposit = async () => {
    if (!userId) return setError('Please sign in to continue.');
    const amt = parseFloat(dgccAmount);
    if (!amt || amt <= 0) return setError('Please enter a valid amount.');
    if (!isConnected) return open();
    
    setLoading(true); setError('');
    try {
      const treasuryAddress = '0xe34899de6cd7c85e2e353db866c53a9e082be6b5';
      const txHash = await sendTransactionAsync({
        to: treasuryAddress,
        value: parseEther(dgccAmount.toString())
      });
      
      const res = await fetch(`${API_URL}/api/billing/verify-dgcc-deposit`, {
        method: 'POST', headers: getAuthHeaders(userId), body: JSON.stringify({ txHash, expectedAmount: amt }),
      });
      const data = await res.json();
      if (data.success) {
        setDgccAmount('');
        if (refreshBalance) refreshBalance();
        alert(`Successfully deposited! You earned ${data.credits} DAG Credits.`);
      } else {
        setError(data.error || 'Failed to verify transaction.');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Transaction failed or rejected.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnergyBridge = async () => {
    if (!userId) return setError('Please sign in to continue.');
    const amt = parseFloat(energyAmount);
    if (!amt || amt <= 0) return setError('Please enter a valid amount.');
    if (!isConnected) return open();
    
    setLoading(true); setError('');
    try {
      const message = `I authorize bridging ${amt} Energy to DAGGPT.\nWallet: ${address}\nTimestamp: ${Date.now()}`;
      const signature = await signMessageAsync({ message });
      
      const res = await fetch(`${API_URL}/api/billing/bridge-energy`, {
        method: 'POST', headers: getAuthHeaders(userId), body: JSON.stringify({ amount: amt, signature, message }),
      });
      const data = await res.json();
      if (data.success) {
        setEnergyAmount('');
        if (refreshBalance) refreshBalance();
        refetchEnergy();
        alert(`Successfully bridged ${amt} Energy!`);
      } else {
        setError(data.error || 'Failed to bridge energy.');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Signature rejected or bridging failed.');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (num, maxFrac = 2) => (num !== undefined && num !== null) ? Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: maxFrac }) : '0.00';

  return (
    <div style={{ width: '100%', minHeight: '100%', display: 'flex', flexDirection: 'column', background: '#F5F5F7', padding: '40px' }}>
      
      {/* 100% Width Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 700, color: '#1D1D1F', margin: '0 0 8px', letterSpacing: '-0.5px' }}>Wallet Dashboard</h1>
        <p style={{ fontSize: '15px', color: '#86868B', margin: 0, fontWeight: 500 }}>Manage your fiat and crypto portfolio seamlessly.</p>
      </div>

      {/* Fluid 12-Column CSS Grid (100% Width) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '24px', alignItems: 'stretch' }}>
        
        {/* ================= ROW 1 ================= */}
        
        {/* 1. Hero Balance with embedded Quick Actions (Span 8) */}
        <BentoBox variant="dark" style={{ gridColumn: 'span 8', color: '#FFFFFF', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: 0 }}>
          <div style={{ position: 'absolute', top: -100, right: -50, width: 400, height: 400, background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0) 70%)', zIndex: 0 }} />
          
          {/* Left Side: Balance Data */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, padding: '32px' }}>
            
            {/* Top Left Text */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#86868B', fontWeight: 500, fontSize: '13px', letterSpacing: '0.5px', position: 'absolute', top: '32px', left: '32px' }}>
              <Wallet size={16} /> Total Estimated Portfolio Value
            </div>

            {/* Massive Centered Value */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: '128px', fontWeight: 700, letterSpacing: '-3px', lineHeight: 1, color: '#FFFFFF' }}>
                <span style={{ fontSize: '72px', color: '#86868B', marginRight: '8px', verticalAlign: 'top' }}>$</span>{fmt(dgccData?.balance || 0)}
              </div>
            </div>

            {/* Bottom Pills */}
            <div style={{ position: 'absolute', bottom: '32px', left: 0, width: '100%', display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '100px', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: '#86868B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>DGCC Available</span>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#FFF' }}>{fmt(dgccData?.balance)}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '100px', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: '#86868B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Energy Owned</span>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#FFF' }}>0.00 <span style={{ color: '#86868B', fontSize: '12px' }}>ENRG</span></span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '100px', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: '#86868B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Crypto Assets</span>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#FFF' }}>0.00 <span style={{ color: '#86868B', fontSize: '12px' }}>USDT</span></span>
              </div>
            </div>
          </div>

          {/* Right Side: Quick Actions Grid */}
          <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', padding: '32px', width: '380px', alignContent: 'center' }}>
            {[
              { icon: ArrowDown, label: 'Deposit Funds' },
              { icon: ArrowUpRight, label: 'Withdraw Assets' },
              { icon: Server, label: 'Host a GPU' },
              { icon: Cpu, label: 'Rent a GPU' }
            ].map((action, i) => (
              <motion.div key={i} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '24px', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', cursor: 'pointer', transition: 'background 0.2s', ':hover': { background: 'rgba(255,255,255,0.08)' }, aspectRatio: '1 / 1' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFF' }}><action.icon size={24} strokeWidth={1.5} /></div>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#FFF', textAlign: 'center' }}>{action.label}</span>
              </motion.div>
            ))}
          </div>
        </BentoBox>

        {/* 2. Top Up Billing (Span 4) */}
        <BentoBox delay={0.1} noPadding style={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column', height: '500px' }}>
          <div style={{ padding: '28px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1D1D1F', margin: 0 }}>Add Funds</h3>
            <div style={{ display: 'flex', background: '#F5F5F7', borderRadius: '12px', padding: '4px', gap: '4px' }}>
              {[{ id: 'fiat', label: 'Fiat' }, { id: 'crypto', label: 'Crypto' }, { id: 'dgcc', label: 'DGCC' }, { id: 'energy', label: 'Energy' }].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '8px 18px', background: activeTab === tab.id ? '#FFFFFF' : 'transparent', border: 'none', cursor: 'pointer',
                    borderRadius: '10px', color: activeTab === tab.id ? '#1D1D1F' : '#86868B',
                    fontWeight: 600, fontSize: '14px', transition: 'all 0.2s', boxShadow: activeTab === tab.id ? '0 2px 8px rgba(0,0,0,0.04)' : 'none'
                  }}>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ padding: '24px 28px 28px', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <AnimatePresence mode="wait">
              {activeTab === 'fiat' && (
                <motion.div key="fiat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '24px' }}>
                    {DGCC_PACKS.map(pack => {
                      const isActive = !isCustom && selectedId === pack.id;
                      return (
                        <div key={pack.id} onClick={() => { setSelectedId(pack.id); setIsCustom(false); setCustomAmount(''); setError(''); }}
                          style={{ padding: '12px 8px', borderRadius: '12px', cursor: 'pointer', background: isActive ? '#000' : '#F9F9FB', color: isActive ? '#FFF' : '#1D1D1F', border: `1px solid ${isActive ? '#000' : 'rgba(0,0,0,0.06)'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'all 0.2s' }}>
                          <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '2px', opacity: isActive ? 0.8 : 1 }}>{pack.label}</div>
                          <div style={{ fontSize: '16px', fontWeight: 700 }}>${pack.amount}</div>
                        </div>
                      );
                    })}

                    <div onClick={() => { setIsCustom(true); setError(''); }}
                      style={{ padding: '12px 8px', borderRadius: '12px', cursor: 'pointer', background: isCustom ? '#000' : '#F9F9FB', color: isCustom ? '#FFF' : '#1D1D1F', border: `1px solid ${isCustom ? '#000' : 'rgba(0,0,0,0.06)'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'all 0.2s' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '2px', opacity: isCustom ? 0.8 : 1 }}>Custom</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '16px', fontWeight: 700, marginRight: '2px' }}>$</span>
                        <input type="text" inputMode="numeric" pattern="[0-9]*" value={customAmount} onChange={e => { setCustomAmount(e.target.value.replace(/[^0-9.]/g, '')); setIsCustom(true); setError(''); }} placeholder="0"
                          style={{ width: `${Math.max(1.5, customAmount.toString().length)}ch`, border: 'none', outline: 'none', background: 'transparent', fontSize: '16px', fontWeight: 700, color: isCustom ? '#FFF' : '#1D1D1F', padding: 0, textAlign: 'center' }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ background: '#F5F5F7', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Exchange Rate</span>
                      <span style={{ color: '#1D1D1F', fontWeight: 600 }}>1 DGCC = $1.00 USD</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Estimated Receipt</span>
                      <span style={{ color: '#1D1D1F', fontWeight: 600 }}>{amount.toFixed(2)} DGCC</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Transaction Fee</span>
                      <span style={{ color: '#0071E3', fontWeight: 600 }}>Covered by DAGGPT</span>
                    </div>
                  </div>

                  {error && <div style={{ color: '#FF3B30', fontSize: '12px', fontWeight: 500, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}><Activity size={14} /> {error}</div>}
                  <button onClick={handleStripeCheckout} disabled={!valid || loading}
                    style={{ width: '100%', padding: '16px 0', border: 'none', borderRadius: '12px', background: valid ? '#635BFF' : '#E5E5EA', color: valid ? '#FFFFFF' : '#8E8E93', fontSize: '15px', fontWeight: 600, cursor: valid ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', transition: 'all 0.2s', marginTop: 'auto', boxShadow: valid ? '0 4px 14px rgba(99, 91, 255, 0.3)' : 'none' }}>
                    {loading ? 'Processing...' : (
                      <>
                        Pay ${amount.toFixed(2)} with
                        <img src="/stripe_logo.svg" alt="Stripe" style={{ height: '36px', marginLeft: '6px', opacity: valid ? 1 : 0.5 }} />
                      </>
                    )}
                  </button>
                </motion.div>
              )}

              {activeTab === 'crypto' && (
                <motion.div key="crypto" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '16px' }}>
                    {[
                      { id: 'erc20', label: 'ERC20', logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.svg?v=025', color: '#627EEA' },
                      { id: 'bep20', label: 'BEP20', logo: 'https://cryptologos.cc/logos/bnb-bnb-logo.svg?v=025', color: '#F3BA2F' },
                      { id: 'trc20', label: 'TRC20', logo: 'https://cryptologos.cc/logos/tron-trx-logo.svg?v=025', color: '#FF060A' }
                    ].map(net => (
                      <button key={net.id} onClick={() => setCryptoNetwork(net.id)}
                        style={{ padding: '12px', borderRadius: '10px', border: `1px solid ${cryptoNetwork === net.id ? net.color : 'rgba(0,0,0,0.06)'}`, background: cryptoNetwork === net.id ? '#FFF' : '#F9F9FB', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s', boxShadow: cryptoNetwork === net.id ? `0 0 0 1px ${net.color}` : 'none' }}>
                        <img src={net.logo} alt={net.label} style={{ width: 18, height: 18 }} />
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#1D1D1F' }}>{net.label}</span>
                      </button>
                    ))}
                  </div>

                  <div style={{ padding: '12px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '8px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1D1D1F', paddingLeft: '4px', whiteSpace: 'nowrap' }}>USDT Amount:</div>
                    <input type="number" value={cryptoAmount} onChange={e => setCryptoAmount(e.target.value)} placeholder="0.00" style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '16px', fontWeight: 600, color: '#1D1D1F', textAlign: 'right' }} />
                  </div>

                  <div style={{ background: '#F5F5F7', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Exchange Rate</span>
                      <span style={{ color: '#1D1D1F', fontWeight: 600 }}>1 DGCC = 1.00 USDT</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Selected Network</span>
                      <span style={{ color: '#1D1D1F', fontWeight: 600 }}>{cryptoNetwork.toUpperCase()}</span>
                    </div>
                  </div>

                  <div style={{ background: '#FFF3CD', border: '1px solid #FFEEBA', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                    <p style={{ fontSize: '11px', color: '#856404', margin: 0, lineHeight: 1.4 }}>
                      <strong>WARNING:</strong> Only deposit from Personal Wallets (MetaMask, Trust Wallet, SafePal, TokenPocket). DO NOT initiate deposits from Exchanges (Binance, Coinbase, etc.). DAGGPT is not responsible for funds lost if deposited from an exchange wallet.
                    </p>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '16px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={cryptoChecked} onChange={e => setCryptoChecked(e.target.checked)} style={{ marginTop: '2px', accentColor: '#1D1D1F' }} />
                    <span style={{ fontSize: '12px', color: '#86868B', lineHeight: 1.4, flex: 1 }}>I confirm I am depositing from a personal Web3 wallet and accept the risks.</span>
                  </label>

                  <button onClick={handleNowPaymentsCheckout} disabled={!cryptoChecked || !cryptoAmount || cryptoAmount < 5 || loading} 
                    style={{ width: '100%', padding: '16px 0', border: 'none', borderRadius: '12px', background: (cryptoChecked && cryptoAmount >= 5) ? 'linear-gradient(135deg, #0052D4 0%, #000000 100%)' : '#E5E5EA', color: (cryptoChecked && cryptoAmount >= 5) ? '#FFFFFF' : '#8E8E93', fontSize: '15px', fontWeight: 600, cursor: (cryptoChecked && cryptoAmount >= 5) ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s', marginTop: 'auto', boxShadow: (cryptoChecked && cryptoAmount >= 5) ? '0 4px 14px rgba(0, 82, 212, 0.4)' : 'none' }}>
                    {loading ? 'Processing...' : (
                      <>
                        Pay with
                        <img src="/nowpayments_logo.svg" alt="NOWPayments" style={{ height: '22px', marginLeft: '6px', opacity: (cryptoChecked && cryptoAmount >= 5) ? 1 : 0.5 }} />
                      </>
                    )}
                  </button>
                </motion.div>
              )}

              {activeTab === 'dgcc' && (
                <motion.div key="dgcc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '16px', borderRadius: '12px', background: '#F9F9FB', border: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#86868B' }}>
                      Connected Wallet
                      {isConnected && <div style={{ color: '#1D1D1F', marginTop: '4px' }}>Balance: {formattedDgcc} DGCC</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <appkit-button balance="hide" />
                    </div>
                  </div>
                  
                  <div style={{ padding: '12px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '8px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1D1D1F', paddingLeft: '4px', whiteSpace: 'nowrap' }}>Deposit Amount:</div>
                    <input type="number" value={dgccAmount} onChange={e => setDgccAmount(e.target.value)} placeholder="0.00" style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '16px', fontWeight: 600, color: '#1D1D1F', textAlign: 'right' }} />
                  </div>
                  
                  <div style={{ background: '#F5F5F7', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Network</span>
                      <span style={{ color: '#1D1D1F', fontWeight: 600 }}>DAG Chain (Mainnet)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Estimated Gas Fee</span>
                      <span style={{ color: '#1D1D1F', fontWeight: 600 }}>~0.001 DGCC</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Processing Time</span>
                      <span style={{ color: '#0071E3', fontWeight: 600 }}>&lt; 3 Seconds</span>
                    </div>
                  </div>
                  
                  {error && <div style={{ color: '#FF3B30', fontSize: '12px', fontWeight: 500, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}><Activity size={14} /> {error}</div>}

                  <button onClick={isConnected ? handleDgccDeposit : () => open()} disabled={loading || (isConnected && (!dgccAmount || dgccAmount <= 0))} 
                    style={{ width: '100%', padding: '16px 0', border: 'none', borderRadius: '12px', background: (!isConnected || dgccAmount > 0) ? 'linear-gradient(135deg, #1927DE 0%, #C86DD7 100%)' : '#E5E5EA', color: (!isConnected || dgccAmount > 0) ? '#FFFFFF' : '#8E8E93', fontSize: '15px', fontWeight: 600, cursor: (!isConnected || dgccAmount > 0) ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s', marginTop: 'auto', boxShadow: (!isConnected || dgccAmount > 0) ? '0 4px 14px rgba(200, 109, 215, 0.4)' : 'none' }}>
                    {loading ? 'Processing...' : (isConnected ? 'Deposit DGCC' : 'Connect Wallet')}
                    <img src="/dagchain_logo.png" alt="DGCC" style={{ height: '32px', marginLeft: '6px', opacity: (!isConnected || dgccAmount > 0) ? 1 : 0.5 }} />
                  </button>
                </motion.div>
              )}

              {activeTab === 'energy' && (
                <motion.div key="energy" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1D1D1F', margin: '0 0 4px' }}>Energy Bridge</h3>
                  <p style={{ fontSize: '13px', color: '#86868B', lineHeight: 1.5, margin: '0 0 24px' }}>Securely bridge your accumulated Energy from the DAG Energy platform for AI generations.</p>
                  
                  <div style={{ background: '#F9F9FB', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.06)', padding: '16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '13px', alignItems: 'center' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Source Wallet</span>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <appkit-button balance="hide" />
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Available Energy</span>
                      <span style={{ color: '#0071E3', fontWeight: 600 }}>{isConnected ? `${formattedEnergy} ENRG` : 'Wallet Not Connected'}</span>
                    </div>
                  </div>
                  
                  <div style={{ padding: '12px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '8px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1D1D1F', paddingLeft: '4px', whiteSpace: 'nowrap' }}>Transfer Amount:</div>
                    <input type="number" value={energyAmount} onChange={e => setEnergyAmount(e.target.value)} placeholder="0" style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '16px', fontWeight: 600, color: '#1D1D1F', textAlign: 'right' }} />
                  </div>

                  <div style={{ background: '#F5F5F7', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Conversion Rate</span>
                      <span style={{ color: '#1D1D1F', fontWeight: 600 }}>1 ENRG = 0.00002 USDT</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#86868B', fontWeight: 500 }}>Destination</span>
                      <span style={{ color: '#1D1D1F', fontWeight: 600 }}>DAG GPT Engine</span>
                    </div>
                  </div>

                  {error && <div style={{ color: '#FF3B30', fontSize: '12px', fontWeight: 500, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}><Activity size={14} /> {error}</div>}

                  <button onClick={isConnected ? handleEnergyBridge : () => open()} disabled={loading || (isConnected && (!energyAmount || energyAmount <= 0))}
                    style={{ width: '100%', padding: '16px 0', border: 'none', borderRadius: '12px', background: (!isConnected || energyAmount > 0) ? '#00D26A' : '#E5E5EA', color: (!isConnected || energyAmount > 0) ? '#FFFFFF' : '#8E8E93', fontSize: '15px', fontWeight: 600, cursor: (!isConnected || energyAmount > 0) ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s', marginTop: 'auto', boxShadow: (!isConnected || energyAmount > 0) ? '0 4px 14px rgba(0, 210, 106, 0.4)' : 'none' }}>
                    {loading ? 'Bridging...' : (isConnected ? 'Bridge Energy' : 'Connect Wallet')}
                    <img src="/energy_logo.png" alt="Energy" style={{ height: '18px', marginLeft: '6px', opacity: (!isConnected || energyAmount > 0) ? 1 : 0.5 }} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </BentoBox>

        {/* ================= ROW 2 ================= */}
        
        {/* 3. Asset Allocation (Span 4) */}
        <BentoBox delay={0.2} style={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1D1D1F', margin: '0 0 24px', letterSpacing: '-0.3px' }}>Asset Allocation</h3>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DonutChart />
          </div>
        </BentoBox>

        {/* 7. Live Exchange Rates (Span 4) */}
        <BentoBox delay={0.3} style={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1D1D1F', margin: 0, letterSpacing: '-0.3px' }}>Exchange Rates</h3>
            <div style={{ fontSize: '11px', color: '#10B981', display: 'flex', alignItems: 'center', gap: '4px', background: '#D1FAE5', padding: '4px 8px', borderRadius: '6px', fontWeight: 600 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981' }} /> Live
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#F9F9FB', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '10px', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}><Coins size={18} color="#1D1D1F" /></div>
                <div><div style={{ fontSize: '13px', fontWeight: 700, color: '#1D1D1F' }}>1 DGCC</div><div style={{ fontSize: '11px', color: '#86868B' }}>DAGGPT Coin</div></div>
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#1D1D1F' }}>$1.00 USD</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#F9F9FB', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '10px', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}><Zap size={18} color="#1D1D1F" /></div>
                <div><div style={{ fontSize: '13px', fontWeight: 700, color: '#1D1D1F' }}>1 ENRG</div><div style={{ fontSize: '11px', color: '#86868B' }}>Energy Token</div></div>
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#1D1D1F' }}>${fmt(0.00002, 5)} USD</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#F9F9FB', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '10px', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}><Server size={18} color="#1D1D1F" /></div>
                <div><div style={{ fontSize: '13px', fontWeight: 700, color: '#1D1D1F' }}>1 Node</div><div style={{ fontSize: '11px', color: '#86868B' }}>Sentinel Hosting</div></div>
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#1D1D1F' }}>$25.00 USD</div>
            </div>
          </div>
        </BentoBox>

        {/* 5. Redeem Promo (Span 4) */}
        <BentoBox delay={0.4} style={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <Gift size={20} color="#1D1D1F" />
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1D1D1F', margin: 0, letterSpacing: '-0.3px' }}>Redeem Promo</h3>
            </div>
            <p style={{ fontSize: '13px', color: '#86868B', margin: '0 0 24px', lineHeight: 1.5 }}>Have a gift card or affiliate voucher? Redeem it here for instant DGCC or ENRG.</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input type="text" placeholder="Enter code (e.g. DAG2026)" style={{ flex: 1, padding: '16px 16px', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '12px', fontSize: '14px', outline: 'none' }} />
              <button style={{ padding: '0 24px', background: '#1D1D1F', color: '#FFF', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>Apply</button>
            </div>
          </div>
        </BentoBox>

        {/* ================= ROW 3 ================= */}

        {/* 6. Deposit & Transaction History (Span 12) */}
        <BentoBox delay={0.5} style={{ gridColumn: 'span 12', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1D1D1F', margin: 0, letterSpacing: '-0.3px' }}>Deposit History</h3>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ background: '#F5F5F7', border: 'none', borderRadius: '8px', padding: '8px 12px', color: '#1D1D1F', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}><Download size={14} /> Export CSV</button>
              <button style={{ background: 'transparent', border: 'none', color: '#0071E3', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>See All <ArrowRight size={14} /></button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr', padding: '0 16px 16px', fontSize: '13px', fontWeight: 700, color: '#86868B', textTransform: 'uppercase', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <div>Transaction ID</div>
              <div>Method & Network</div>
              <div>Status</div>
              <div style={{ textAlign: 'right' }}>Amount</div>
            </div>

            {transactions.filter(tx => tx.amount > 0).length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#86868B', fontSize: '14px' }}>No transactions found.</div>
            ) : transactions.filter(tx => tx.amount > 0).map(tx => {
              const isPositive = tx.amount > 0;
              const isDeposit = tx.type === 'purchase' || tx.type === 'deposit' || tx.type === 'adjustment' || tx.type === 'bonus';
              const Icon = isDeposit ? ArrowDown : Zap;
              const dateStr = new Date(tx.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
              
              let methodText = 'Adjustment / Bonus';
              if (tx.type === 'purchase') methodText = 'Fiat / Stripe';
              if (tx.type === 'deposit') methodText = 'Web3 / DAG Chain';
              if (tx.type === 'deduction') methodText = 'AI Generation';

              return (
                <div key={tx.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr', alignItems: 'center', padding: '20px 16px', borderBottom: '1px solid rgba(0,0,0,0.04)', cursor: 'pointer', transition: 'background 0.2s', ':hover': { background: '#F9F9FB' } }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#F5F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={18} color="#1D1D1F" />
                    </div>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: 600, color: '#1D1D1F', marginBottom: '4px' }}>{tx.type.toUpperCase()}-{tx.id.substring(0,6).toUpperCase()}</div>
                      <div style={{ fontSize: '13px', color: '#86868B' }}>{dateStr}</div>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: '#1D1D1F', marginBottom: '4px' }}>{methodText}</div>
                    <div style={{ fontSize: '13px', color: '#86868B' }}>{tx.description || tx.type}</div>
                  </div>
                  <div><span style={{ padding: '6px 12px', background: '#D1FAE5', color: '#059669', fontSize: '12px', fontWeight: 600, borderRadius: '8px' }}>Completed</span></div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#1D1D1F', marginBottom: '4px' }}>
                      {isPositive ? '+' : ''}{tx.amount.toFixed(2)}
                    </div>
                    <div style={{ fontSize: '13px', color: '#86868B' }}>DGCC {isPositive ? 'Issued' : 'Spent'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </BentoBox>
      </div>
    </div>
  );
}
